import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-2xs font-regular w-fit whitespace-nowrap transition-[color,box-shadow]',
  {
    variants: {
      variant: {
        liquid: '',
        solid: '',
        text: '',
        custom: '',
      },
      size: {
        sm: 'p-3xs text-2xs',
        md: 'p-2xs text-xs',
        lg: 'p-2xs text-sm',
      },
      width: {
        auto: 'w-fit',
      },
      color: {
        base: '',
        neutral: '',
        positive: '',
        negative: '',
        alert: '',
        disabled: '',
        custom: '',
      },
    },
    defaultVariants: {
      variant: 'liquid',
      size: 'md',
      color: 'base',
      width: 'auto',
    },
    compoundVariants: [
      {
        variant: 'liquid',
        color: 'base',
        className: 'bg-base-surface text-base-surface-100',
      },
      {
        variant: 'solid',
        color: 'base',
        className: 'bg-base-main text-base-main-100',
      },
      {
        variant: 'text',
        color: 'base',
        className: 'text-base-surface-100',
      },
      {
        variant: 'liquid',
        color: 'neutral',
        className: 'bg-neutral-surface text-neutral-surface-100',
      },
      {
        variant: 'solid',
        color: 'neutral',
        className: 'bg-neutral-main text-neutral-main-100',
      },
      {
        variant: 'text',
        color: 'neutral',
        className: 'text-neutral-surface-100',
      },
      {
        variant: 'liquid',
        color: 'positive',
        className: 'bg-positive-surface text-positive-surface-100',
      },
      {
        variant: 'solid',
        color: 'positive',
        className: 'bg-positive-main text-positive-main-100',
      },
      {
        variant: 'text',
        color: 'positive',
        className: 'text-positive-surface-100',
      },
      {
        variant: 'liquid',
        color: 'negative',
        className: 'bg-negative-surface text-negative-surface-100',
      },
      {
        variant: 'solid',
        color: 'negative',
        className: 'bg-negative-main text-negative-main-100',
      },
      {
        variant: 'text',
        color: 'negative',
        className: 'text-negative-surface-100',
      },
      {
        variant: 'liquid',
        color: 'alert',
        className: 'bg-alert-surface text-alert-surface-100',
      },
      {
        variant: 'solid',
        color: 'alert',
        className: 'bg-alert-main text-alert-main-100',
      },
      {
        variant: 'text',
        color: 'alert',
        className: 'text-alert-surface-100',
      },
      {
        variant: 'liquid',
        color: 'disabled',
        className: 'bg-disabled-surface text-disabled-surface-100',
      },
      {
        variant: 'solid',
        color: 'disabled',
        className: 'bg-disabled-main text-disabled-main-100',
      },
      {
        variant: 'text',
        color: 'disabled',
        className: 'text-disabled-surface-100',
      },
      {
        variant: 'custom',
        color: 'custom',
        className: '',
      },
    ],
  },
)
export interface BadgeProps
  extends Omit<
      React.ComponentProps<'span'>,
      keyof VariantProps<typeof badgeVariants>
    >,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean
}

export function Badge({
  className,
  variant,
  size,
  color,
  asChild = false,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      className={twMerge(badgeVariants({ variant, size, color, className }))}
      {...props}
    />
  )
}
